/**
 * OnboardingTour — Driver.js spotlight tour for first-login org_admin users.
 * Shown only once (onboarding_completed === false). Calls
 * PATCH /auth/onboarding-complete on finish or dismiss.
 *
 * Variant A: spotlight + explain + Next button (no forced interaction).
 * Steps target IDs injected into Layout.jsx sidebar and topbar.
 */

import { useEffect } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { authApi } from '../api/httpClient';

// ── Custom CSS overrides to match the app's visual theme ─────────────────────
const TOUR_CSS = `
  .cng-popover.driver-popover {
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    border-radius: 16px;
    padding: 20px 22px 18px;
    background: #fff;
    border: 1px solid rgba(200,30,120,0.12);
    box-shadow: 0 16px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(123,47,228,0.06);
    max-width: 320px;
  }
  .cng-popover .driver-popover-title {
    font-size: 16px;
    font-weight: 800;
    color: #1A1222;
    margin-bottom: 8px;
    line-height: 1.3;
  }
  .cng-popover .driver-popover-description {
    font-size: 13.5px;
    color: #5C5365;
    line-height: 1.65;
    margin-bottom: 0;
  }
  .cng-popover .driver-popover-progress-text {
    font-size: 12px;
    color: #8E8597;
    font-weight: 600;
  }
  .cng-popover .driver-popover-prev-btn {
    background: var(--surface-2, #F6F2FB);
    color: #5C5365;
    border: 1.5px solid #ECE6F2;
    border-radius: 9px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: background 0.15s;
  }
  .cng-popover .driver-popover-prev-btn:hover {
    background: #ECE6F2;
  }
  .cng-popover .driver-popover-next-btn {
    background: linear-gradient(115deg, #7B2FE4, #C71D8E, #F5176B);
    color: #fff;
    border: none;
    border-radius: 9px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(200,30,120,0.28);
    transition: opacity 0.15s;
  }
  .cng-popover .driver-popover-next-btn:hover { opacity: 0.88; }
  .cng-popover .driver-popover-close-btn {
    color: #9CA3AF;
    font-size: 18px;
    line-height: 1;
    top: 14px;
    right: 14px;
  }
  .cng-popover .driver-popover-close-btn:hover { color: #5C5365; }
  .cng-popover .driver-popover-footer {
    margin-top: 16px;
    gap: 8px;
  }
  .cng-popover .driver-popover-arrow-side-left.driver-popover-arrow  { border-left-color: #fff; }
  .cng-popover .driver-popover-arrow-side-right.driver-popover-arrow { border-right-color: #fff; }
  .cng-popover .driver-popover-arrow-side-top.driver-popover-arrow   { border-top-color: #fff; }
  .cng-popover .driver-popover-arrow-side-bottom.driver-popover-arrow { border-bottom-color: #fff; }
  .driver-overlay { opacity: 0.72 !important; }
`;

// ── Tour steps ────────────────────────────────────────────────────────────────
const STEPS = [
  {
    // No element — centered welcome card
    popover: {
      title: '👋 Welcome to Click n Go!',
      description: "You're all set up. Let's take a 60-second tour so you know where everything lives.",
      side: 'over',
      align: 'center',
    },
  },
  {
    element: '#nav-events',
    popover: {
      title: '📅 Your Events',
      description: 'All your events live here. Click "New Event" to launch the guided setup wizard — details, ticket tiers, custom registration fields, and publishing in one flow.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#nav-wallet',
    popover: {
      title: '🪙 Token Wallet',
      description: 'Every confirmed registration costs 1 token from your org wallet. You start with 50 free tokens. Purchase more here before a big event.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#topbar-token-balance',
    popover: {
      title: '💰 Live Token Balance',
      description: 'Your current token balance is always visible here. Monitor it so you never run out mid-registration.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: '#topbar-bell',
    popover: {
      title: '🔔 Notifications',
      description: 'Real-time alerts for new registrations, waitlist promotions, team invites, and event activity. The badge shows unread count.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: '#nav-team',
    popover: {
      title: '👥 Your Team',
      description: 'Invite event admins to manage specific events, and volunteers for scanning on event day. No extra cost per team member.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#nav-settings',
    popover: {
      title: '⚙️ Org Settings',
      description: 'Upload your logo, configure your organisation name, and connect your Razorpay account for paid ticket sales.',
      side: 'right',
      align: 'start',
    },
  },
  {
    // No element — final card
    popover: {
      title: "🚀 You're ready!",
      description: "That's the core of it. Head to Events → New Event to create your first event, share the registration link, and watch attendees roll in.",
      side: 'over',
      align: 'center',
    },
  },
];

// ── Component ─────────────────────────────────────────────────────────────────
export default function OnboardingTour({ onDone }) {
  useEffect(() => {
    // Inject theme CSS
    const styleEl = document.createElement('style');
    styleEl.textContent = TOUR_CSS;
    document.head.appendChild(styleEl);

    const handleFinish = async () => {
      try { await authApi.completeOnboarding(); } catch {}
      onDone();
    };

    const driverObj = driver({
      showProgress:   true,
      allowClose:     true,
      overlayOpacity: 0.65,
      stagePadding:   10,
      stageRadius:    12,
      popoverClass:   'cng-popover',
      steps:          STEPS,
      onDestroyed:    handleFinish,
    });

    // Short delay so the DOM is fully painted before we start highlighting
    const t = setTimeout(() => driverObj.drive(), 700);

    return () => {
      clearTimeout(t);
      styleEl.remove();
      if (driverObj.isActive()) driverObj.destroy();
    };
  }, [onDone]);

  // Driver.js renders its own DOM overlay — this component renders nothing
  return null;
}
